import type { PlaybackStop } from '../contracts';
import type { SpeakableSentence } from './speakable';
import { observeScheduledAudio } from './lane-playback-timing';
import type { PlaybackProfile } from '../lane-timing';
import { createPlaybackReference } from './reference';
import { createEchoReference } from './echo-reference';

type Scheduled = {
  index: number;
  sentence: SpeakableSentence;
  start: number;
  duration: number;
  node: AudioBufferSourceNode | null;
  complete: boolean;
  notified: boolean;
  ended: boolean;
  offset: number;
  playbackId: string | null;
};
export function createAudioSchedule(
  context: AudioContext,
  messageId: string,
  changed: () => void,
  highlight: (sentence: SpeakableSentence | null) => void,
) {
  const gain = context.createGain();
  const reference = createPlaybackReference(context);
  const echoReference = createEchoReference(context);
  gain.connect(context.destination);
  const records: Scheduled[] = [];
  const durations = new Map<number, number>();
  const outputDelay = () =>
    Math.max(0, context.outputLatency || 0) + Math.max(0, context.baseLatency || 0);
  let end = context.currentTime;
  let active = 0;
  let last: Scheduled | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let queuedDuration = 0;
  const capacityWaiters: (() => void)[] = [];
  const retire = () => {
    const keep = new Set([last]);
    for (let i = records.length - 1; i >= 0; i--)
      if (
        records[i].ended &&
        !keep.has(records[i]) &&
        records[i].start + records[i].duration < context.currentTime - outputDelay()
      )
        records.splice(i, 1);
  };
  function updateHighlight() {
    if (timer) clearTimeout(timer);
    const current = records.find(
      (row) => row.start <= context.currentTime && context.currentTime < row.start + row.duration,
    );
    if (current) {
      last = current;
      highlight(current.sentence);
    }
    const next = records.find((row) => row.start > context.currentTime);
    if (next)
      timer = setTimeout(updateHighlight, Math.max(1, (next.start - context.currentTime) * 1000));
  }
  return {
    append(
      index: number,
      sentence: SpeakableSentence,
      clip: AudioBuffer,
      complete = true,
      profile?: PlaybackProfile | null,
      playbackId: string | null = null,
    ) {
      const node = context.createBufferSource();
      node.buffer = clip;
      node.connect(gain);
      const start = Math.max(context.currentTime + (active ? 0 : 0.005), end);
      const row: Scheduled = {
        index,
        sentence,
        node,
        start,
        duration: clip.duration,
        complete,
        notified: false,
        ended: false,
        offset: durations.get(index) ?? 0,
        playbackId,
      };
      durations.set(index, row.offset + clip.duration);
      records.push(row);
      active++;
      queuedDuration += clip.duration;
      end = start + clip.duration;
      node.onended = () => {
        active--;
        queuedDuration = Math.max(0, queuedDuration - row.duration);
        row.ended = true;
        last = row;
        node.disconnect();
        row.node = null;
        if (!active) highlight(null);
        if (row.complete && !row.notified) {
          row.notified = true;
          changed();
        }
        while (capacityWaiters.length && queuedDuration < 2) capacityWaiters.shift()!();
        retire();
      };
      node.start(start);
      reference.append(clip, start);
      echoReference.append(clip, start);
      observeScheduledAudio({
        index,
        buffer: clip,
        scheduledAt: start,
        contextNow: context.currentTime,
        profile,
      });
      updateHighlight();
    },
    waitForCapacity(maxSeconds = 2) {
      return queuedDuration < maxSeconds
        ? Promise.resolve()
        : new Promise<void>((resolve) => capacityWaiters.push(resolve));
    },
    complete(index: number) {
      const rows = records.filter((row) => row.index === index);
      const lastRow = rows.at(-1);
      if (!lastRow) return;
      // PCM chunks are started immediately; the final node owns sentence completion.
      lastRow.complete = true;
      if (!lastRow.node && !lastRow.notified) {
        lastRow.notified = true;
        changed();
      }
    },
    pending: () => active,
    playbackLevel: () => reference.level(),
    echoLevel: echoReference.level,
    duck(db: number) {
      gain.gain.value = 10 ** (db / 20);
    },
    position(): PlaybackStop | null {
      const audibleAt = context.currentTime - outputDelay();
      const current = records.find(
        (row) => row.start <= audibleAt && audibleAt < row.start + row.duration,
      );
      const heard = current ?? [...records].reverse().find((row) => row.start <= audibleAt);
      if (!heard) return records.length ? { messageId, sentenceIndex: 0, charOffset: 0 } : null;
      // Until a streamed sentence is fully heard, its start is the only safe
      // character offset: audio sample count does not align words to characters.
      const complete = heard.complete && audibleAt >= heard.start + heard.duration;
      const playedMs = Math.max(
        0,
        Math.floor(
          (heard.offset + Math.min(heard.duration, Math.max(0, audibleAt - heard.start))) * 1000,
        ),
      );
      return {
        messageId,
        sentenceIndex: heard.index,
        charOffset: complete ? heard.sentence.end : heard.sentence.start,
        ...(heard.playbackId ? { playback: { id: heard.playbackId, playedMs } } : {}),
      };
    },
    stop() {
      const position = this.position();
      if (timer) clearTimeout(timer);
      for (const row of records) {
        if (row.node) {
          row.node.onended = null;
          try {
            row.node.stop();
          } catch {}
          row.node.disconnect();
        }
      }
      active = 0;
      queuedDuration = 0;
      capacityWaiters.splice(0).forEach((resolve) => resolve());
      gain.disconnect();
      reference.clear();
      echoReference.clear();
      durations.clear();
      highlight(null);
      return position;
    },
  };
}
